import { useCallback, useMemo } from 'react'

import { useQuery } from '@apollo/client'
import {
  IntegrationsAgreggatedSyncableStateValuesEnum,
  ReadSyncableStateStatisticsDocument,
  SyncableStateUpdatedDocument,
} from 'generated/resources/graphql'
import { print } from 'graphql'

import { useFeatures } from 'modules/core/hooks/useFeatures'
import { useSubscription } from 'modules/core/hooks/useSubscription'
import i18n from 'modules/core/lib/i18n'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useLeadingTrailingDebounce } from 'modules/integrations/hooks/useLeadingTrailingDebounce'

export type SyncStatusPreset = {
  label: string
  filter: { integrationsAgreggatedSyncableStates: IntegrationsAgreggatedSyncableStateValuesEnum[] }
  itemsCount: () => number
}

const EMPTY_PRESETS: SyncStatusPreset[] = []

const translateStatusLabel = (status: IntegrationsAgreggatedSyncableStateValuesEnum): string => {
  switch (status) {
    case 'failed':
      return i18n.t('integrations.filters.sync_status.failed')
    case 'partially_synced':
      return i18n.t('integrations.filters.sync_status.partially_synced')
    case 'outdated':
      return i18n.t('integrations.filters.sync_status.outdated')
    case 'pending':
      return i18n.t('integrations.filters.sync_status.pending')
    case 'synced':
      return i18n.t('integrations.filters.sync_status.synced')
    case 'syncing':
      return i18n.t('integrations.filters.sync_status.syncing')
  }
}

export const useSyncStatusPresets = () => {
  const {
    getDataCollectionSource,
    mappedBaseVariables,
    marketplaceIntegration,
    syncEnabled,
    configuration,
  } = usePrivateIntegrationsSyncContext()

  const { has_dev_syncable_state_presets_realtime_updates } = useFeatures([
    'dev_syncable_state_presets_realtime_updates',
  ])

  const featureEnabled = syncEnabled && has_dev_syncable_state_presets_realtime_updates

  const dataCollectionSource = getDataCollectionSource()
  const document = featureEnabled ? dataCollectionSource?.document : undefined

  const graphqlVariablesJson = useMemo(
    () =>
      document != null
        ? JSON.stringify({
            query: print(document),
            variables: {
              ...mappedBaseVariables,
              marketplaceIntegrationId: Number(marketplaceIntegration?.id ?? 0),
              includeSyncableState: Boolean(true),
              syncableViaIntegrations: Boolean(true),
            },
          })
        : undefined,
    [document, mappedBaseVariables, marketplaceIntegration?.id]
  )

  const skip = !featureEnabled || graphqlVariablesJson === undefined

  const queryVariables = useMemo(
    () => ({
      graphqlVariablesJson: graphqlVariablesJson ?? undefined,
      origin: configuration.origin,
    }),
    [graphqlVariablesJson, configuration.origin]
  )

  const { data: statisticsData, refetch: refetchStatistics } = useQuery(
    ReadSyncableStateStatisticsDocument,
    {
      variables: queryVariables,
      skip,
    }
  )

  const refetch = useCallback(() => {
    if (skip) return
    void refetchStatistics()
  }, [skip, refetchStatistics])

  const DEBOUNCE_DELAY_MS = 1000
  const debouncedRefetch = useLeadingTrailingDebounce(refetch, DEBOUNCE_DELAY_MS)

  useSubscription(SyncableStateUpdatedDocument, {
    skip: !featureEnabled,
    onData: () => {
      debouncedRefetch()
    },
  })

  const syncableStateStatistics =
    statisticsData?.integrations?.syncableStateStatisticsConnection?.nodes

  const presets = useMemo<SyncStatusPreset[]>(() => {
    if (!syncEnabled || !syncableStateStatistics) {
      return EMPTY_PRESETS
    }

    const statusCounts = syncableStateStatistics[0]?.statusCounts ?? []
    const getCount = (status: string) => statusCounts.find((s) => s.status === status)?.count ?? 0

    const statusConfig: Array<{
      status: IntegrationsAgreggatedSyncableStateValuesEnum
      count: number
    }> = [
      { status: 'failed', count: getCount('failed') },
      { status: 'partially_synced', count: getCount('partially_synced') },
      { status: 'outdated', count: getCount('outdated') },
      { status: 'pending', count: getCount('pending') },
      { status: 'synced', count: getCount('synced') },
      { status: 'syncing', count: getCount('syncing') },
    ]

    const filtered = statusConfig.filter((config) => config.count > 0)

    return filtered.map((config) => ({
      label: translateStatusLabel(config.status),
      filter: { integrationsAgreggatedSyncableStates: [config.status] },
      itemsCount: () => config.count,
    }))
  }, [syncEnabled, syncableStateStatistics])

  return {
    presets,
  }
}
